import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { connectionHealthInputs } from '@/hooks/useConnectionAttention';
import { integrationHealthQueryOptions } from '@/hooks/useIntegrationHealth';
import { usePlatformIntegrations } from '@/hooks/usePlatformIntegrations';
import { attentionLabel, deriveConnectionAttention } from '@/lib/platforms/connection-attention';
import { deriveConnectionStatus } from '@/lib/platforms/connection-status';
import { shopifyMissingRequiredScopes } from '@/lib/platforms/shopify-permissions';

export function useConnectionAttentionCount(enabled: boolean): number {
  const { connectedAccounts } = usePlatformIntegrations();

  const inputs = useMemo(
    () =>
      connectedAccounts.map((account) => ({
        account,
        ...connectionHealthInputs(account.platform, account),
      })),
    [connectedAccounts],
  );

  const results = useQueries({
    queries: inputs.map(({ account, options }) => {
      const query = integrationHealthQueryOptions(account.platform, options);
      return { ...query, enabled: enabled && query.enabled };
    }),
  });

  if (enabled && results.some((result) => result.isPending)) return 0;

  return inputs.reduce((count, { account, hasScheduledSync }, index) => {
    const attention = deriveConnectionAttention({
      platform: account.platform,
      label: account.platform,
      account,
      tokenStatus: deriveConnectionStatus(account),
      health: results[index]?.data,
      hasScheduledSync,
      shopifyMissingScopes:
        account.platform === 'shopify' ? shopifyMissingRequiredScopes(account) : [],
    });
    return attentionLabel(attention.issues) ? count + 1 : count;
  }, 0);
}
